/**
 * Version timeline for report detail — status orbs + selectable versions.
 */
import { Box, Chip, Stack, Typography } from "@mui/material";
import type { ReportVersionSummaryDTO } from "@flaha/shared-types";

import { GlassSurface } from "../../../layouts/components/GlassSurface";

interface ReportVersionTimelineProps {
	versions: readonly ReportVersionSummaryDTO[];
	activeVersionNumber: number;
	onSelect: (versionNumber: number) => void;
}

function statusColor(status: string): string {
	const upper = status.toUpperCase();
	if (upper === "READY" || upper === "COMPLETED") return "success.main";
	if (upper === "FAILED" || upper === "ERROR") return "error.main";
	if (upper === "PENDING" || upper === "GENERATING") return "warning.main";
	return "workspace.blue";
}

export function ReportVersionTimeline({
	versions,
	activeVersionNumber,
	onSelect,
}: ReportVersionTimelineProps) {
	const ordered = [...versions].sort(
		(a, b) => b.versionNumber - a.versionNumber
	);

	return (
		<GlassSurface
			component="section"
			aria-labelledby="version-timeline-title"
			intensity="standard"
			depth="raised"
			sx={{ p: 2 }}
			data-testid="report-version-timeline"
		>
			<Typography
				id="version-timeline-title"
				component="h2"
				variant="subtitle1"
				fontWeight={700}
				sx={{ mb: 0.5 }}
			>
				Version timeline
			</Typography>
			<Typography variant="caption" sx={{ color: "workspace.textMuted", display: "block", mb: 2 }}>
				Each version freezes the scientific snapshot used for PDF delivery.
				Select a version to preview it on screen.
			</Typography>

			<Stack
				component="ol"
				spacing={0}
				sx={{ m: 0, p: 0, listStyle: "none", position: "relative" }}
			>
				{ordered.map((v, index) => {
					const active = v.versionNumber === activeVersionNumber;
					return (
						<Box
							component="li"
							key={v.id}
							sx={{
								position: "relative",
								pl: 3.5,
								pb: index === ordered.length - 1 ? 0 : 2,
							}}
						>
							{/* Vertical rail */}
							{index < ordered.length - 1 ? (
								<Box
									aria-hidden="true"
									sx={{
										position: "absolute",
										left: 9,
										top: 18,
										bottom: 0,
										width: 2,
										bgcolor: "workspace.border",
									}}
								/>
							) : null}
							{/* Status orb */}
							<Box
								aria-hidden="true"
								sx={{
									position: "absolute",
									left: 2,
									top: 6,
									width: 16,
									height: 16,
									borderRadius: "50%",
									border: "2px solid",
									borderColor: active ? "primary.main" : statusColor(v.status),
									bgcolor: active ? "primary.main" : "transparent",
									boxShadow: active
										? "0 0 0 4px rgba(119, 169, 87, 0.2)"
										: "none",
								}}
							/>
							<Box
								role="button"
								tabIndex={0}
								onClick={() => onSelect(v.versionNumber)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										onSelect(v.versionNumber);
									}
								}}
								aria-current={active ? "true" : undefined}
								sx={{
									cursor: "pointer",
									p: 1.25,
									borderRadius: 1.25,
									border: "1px solid",
									borderColor: active ? "primary.main" : "workspace.border",
									backgroundColor: active
										? "rgba(119, 169, 87, 0.1)"
										: "rgba(12, 16, 14, 0.25)",
									transition: (theme) => theme.motion.transition.surface,
									"&:hover": {
										borderColor: "workspace.borderStrong",
									},
									"&:focus-visible": {
										outline: "2px solid",
										outlineColor: "info.main",
										outlineOffset: 2,
									},
								}}
							>
								<Stack direction="row" alignItems="center" spacing={1}>
									<Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
										Version {v.versionNumber}
									</Typography>
									<Chip size="small" label={v.status} />
								</Stack>
								<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
									{new Date(v.generatedAt).toLocaleString()}
								</Typography>
								{active ? (
									<Typography
										variant="caption"
										sx={{ display: "block", mt: 0.5, color: "workspace.green", fontWeight: 600 }}
									>
										Showing on-screen preview
									</Typography>
								) : null}
							</Box>
						</Box>
					);
				})}
			</Stack>
		</GlassSurface>
	);
}
